import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuthStore } from '../store/authStore';
import {
    HiHome,
    HiSearch,
    HiStar,
    HiCollection,
    HiDuplicate,
    HiShoppingBag,
    HiClipboardList,
    HiChartBar,
    HiCreditCard,
    HiCog,
    HiLogout,
    HiMenu,
    HiX,
    HiUser,
    HiCloudUpload
} from 'react-icons/hi';
import NotificationsDropdown from '../components/NotificationsDropdown';
import BotonTema from '../components/BotonTema';
import MenuDesplegable from '../components/MenuDesplegable';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';

const navigation = [
    { key: 'dashboard', href: '/dashboard', icon: HiHome },
    { key: 'product_search', href: '/products/search', icon: HiSearch },
    { key: 'winning_products', href: '/products/winning', icon: HiStar },
    { key: 'my_products', href: '/products/imported', icon: HiCollection },
    { key: 'stores', href: '/stores', icon: HiShoppingBag },
    { key: 'orders', href: '/orders', icon: HiClipboardList },
    { key: 'bulk_operations', href: '/bulk', icon: HiDuplicate },
    { key: 'analytics', href: '/analytics', icon: HiChartBar },
    { key: 'subscription', href: '/subscription', icon: HiCreditCard },
];

export default function DashboardLayout() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuthStore();
    const { t } = useTranslation();

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
            {/* Mobile sidebar backdrop */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black/50 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed inset-y-0 left-0 z-50 w-72 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                    }`}
            >
                <div className="flex flex-col h-full">
                    {/* Logo */}
                    <div className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-slate-700">
                        <Link to="/dashboard" className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-accent-500 rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold text-lg">S</span>
                            </div>
                            <span className="text-xl font-bold gradient-text">ShippingFULL</span>
                        </Link>
                        <button
                            onClick={() => setSidebarOpen(false)}
                            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                            <HiX className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
                        {navigation.map((item) => {
                            const isActive = location.pathname === item.href;
                            return (
                                <Link
                                    key={item.key}
                                    to={item.href}
                                    className={`sidebar-link ${isActive ? 'active' : ''}`}
                                    onClick={() => setSidebarOpen(false)}
                                >
                                    <item.icon className="w-5 h-5" />
                                    <span>{t(`sidebar.${item.key}`)}</span>
                                </Link>
                            );
                        })}
                    </nav>

                    {/* User section */}
                    <div className="p-4 border-t border-slate-200 dark:border-slate-700">
                        <Link
                            to="/settings"
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                            <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-accent-400 rounded-full flex items-center justify-center text-white font-medium">
                                {user?.full_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                                    {user?.full_name || 'User'}
                                </p>
                                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                            </div>
                            <HiCog className="w-5 h-5 text-slate-400" />
                        </Link>
                    </div>
                </div>
            </aside>

            {/* Main content */}
            <div className="lg:pl-72">
                {/* Top header */}
                <header className="sticky top-0 z-30 h-16 bg-white/80 dark:bg-slate-800/80 backdrop-blur-lg border-b border-slate-200 dark:border-slate-700">
                    <div className="flex items-center justify-between h-full px-4 lg:px-8">
                        {/* Mobile menu button */}
                        <button
                            onClick={() => setSidebarOpen(true)}
                            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                            <HiMenu className="w-6 h-6" />
                        </button>

                        {/* Subscription badge */}
                        <div className="hidden lg:flex items-center">
                            {user?.subscription_tier === 'pro' ? (
                                <span className="badge bg-gradient-to-r from-primary-500 to-accent-500 text-white px-3 py-1">
                                    PRO
                                </span>
                            ) : location.pathname !== '/subscription' && (
                                /* En la propia página de Suscripción sobra: te
                                   ofrece ir a donde ya estás, y compite con el
                                   botón de verdad que hay en la tarjeta del
                                   plan. Un atajo a la pantalla actual no es un
                                   atajo, es ruido. */
                                <Link
                                    to="/subscription"
                                    className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                                >
                                    {t('dashboard.upgrade_to_pro')} →
                                </Link>
                            )}
                        </div>

                        {/* Right section */}
                        <div className="flex items-center gap-2">
                            <LanguageSwitcher />

                            {/* Estaba escrito aquí a mano, y con
                                `title={isDark ? 'Light mode' : 'Dark mode'}`
                                **en inglés dentro del JSX**: la regla de oro
                                del proyecto dice que todo texto visible pasa
                                por i18n, y ese llevaba meses saltándosela en
                                una de las pantallas más usadas. Ahora es un
                                componente, con su etiqueta traducida y su
                                `aria-label` para los lectores de pantalla. */}
                            <BotonTema />

                            {/* Notifications */}
                            <NotificationsDropdown />

                            {/* Menú de perfil. Se abría solo al pasar el ratón,
                                así que desde un móvil no había forma de cerrar
                                sesión. */}
                            <MenuDesplegable
                                etiqueta={t('common.account_menu')}
                                claseBoton="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                                boton={
                                    <div className="w-8 h-8 bg-gradient-to-br from-primary-400 to-accent-400 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                        {user?.full_name?.[0]?.toUpperCase() || 'U'}
                                    </div>
                                }
                            >
                                {(cerrar) => (
                                    <>
                                        <Link
                                            to="/settings"
                                            role="menuitem"
                                            onClick={cerrar}
                                            className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                                        >
                                            <HiUser className="w-4 h-4" />
                                            {t('settings.title')}
                                        </Link>
                                        <button
                                            type="button"
                                            role="menuitem"
                                            onClick={() => { cerrar(); handleLogout(); }}
                                            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-danger-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                                        >
                                            <HiLogout className="w-4 h-4" />
                                            {t('sidebar.logout')}
                                        </button>
                                    </>
                                )}
                            </MenuDesplegable>
                        </div>
                    </div>
                </header>

                {/* Page content */}
                <main className="p-4 lg:p-8">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
